import { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button, ButtonVariants } from '@carboledger/ui';
import { EmptyState, LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import {
  useGetCustomerQuery,
  useGetCustomerSiteFormQuery,
  useUpdateCustomerSiteMutation,
} from '../api/customersApi';
import { CustomerSitesForm } from '../components/CustomerSitesForm';
import { useExistingSiteIds, useGoToCustomerSites } from '../hooks';

// Manage customers ▸ customer ▸ Edit customer sites — one saved site's card.
export const EditCustomerSitePage = () => {
  const t = useTranslations();
  const { customerId, siteId } = useParams();

  const siteArg = useMemo(() => ({ customerId, siteId }), [customerId, siteId]);
  const { data: customer, isLoading: isLoadingCustomer } = useGetCustomerQuery(customerId);
  const { data: values, isLoading: isLoadingSite, isError } = useGetCustomerSiteFormQuery(siteArg);
  const [updateSite, { isLoading: isSubmitting }] = useUpdateCustomerSiteMutation();
  const existingIds = useExistingSiteIds(customerId, siteId);
  const goToSites = useGoToCustomerSites(customerId);

  const handleSubmit = useCallback(
    async (nextValues) => {
      const result = await updateSite({ customerId, siteId, values: nextValues });
      if (result.error) return;
      goToSites();
    },
    [updateSite, customerId, siteId, goToSites],
  );

  if (isLoadingCustomer || isLoadingSite) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !values || !customer) {
    return (
      <EmptyState
        title={t('CUSTOMER_SITES_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToSites}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <CustomerSitesForm
      isEdit
      title={t('CUSTOMER_SITES_EDIT_TITLE', { customerName: customer.customerName })}
      initialValues={values}
      existingIds={existingIds}
      onSubmit={handleSubmit}
      onClose={goToSites}
      isSubmitting={isSubmitting}
    />
  );
};
